import moment from 'moment'
import { SkudCardEndpoint } from '@/views/skud/shared/endpoint_services/skud-card-endpoint-service'
import { SkudPassEndpoint } from '@/views/skud/shared/endpoint_services/skud-pass-endpoint-service'
import { envelopeError } from '@/views/skud/shared/modules/skud-http'
import { CredentialKind, CredentialIssuer, CredentialState, GrantState, enumText } from '@/views/skud/shared/models/enums'

const DATE_FMT = 'DD.MM.YYYY HH:mm:ss'

function fmt (v) {
  return v ? moment(v).format(DATE_FMT) : null
}

function mapCredential (c) {
  return Object.assign({}, c, {
    kind_text: enumText(CredentialKind, c.kind),
    issuer_text: enumText(CredentialIssuer, c.issuer),
    replaced_at: fmt(c.replaced_at),
    replaced_until: fmt(c.replaced_until)
  })
}

// active_uids/replaced/blocked_uids приходят без поля state — проставляем по источнику.
const withState = (arr, state) => (arr || []).map(c => Object.assign(mapCredential(c), {
  state: state,
  state_text: enumText(CredentialState, state)
}))

export class SkudCardService {
  constructor () {
    this.cardEndpoint = new SkudCardEndpoint()
    this.passEndpoint = new SkudPassEndpoint()
    this.error = null
  }

  // Список карт места (группировка по субъекту). -> [item] (null -> []).
  async getCards (placeId, filters) {
    const params = Object.assign({}, filters, { limit: 500 })
    // Дата «по» из фильтра — без времени, включаем весь день.
    if (params.to && params.to.length === 10) params.to += 'T23:59:59'
    const res = await this.cardEndpoint.getCards(placeId, params)
    if (!res) {
      this.error = envelopeError(this.cardEndpoint.lastError)
      return []
    }
    this.error = null
    return (res.items || []).map(item => Object.assign({}, item, {
      // grant-поля отсутствуют у субъекта без пропуска (api_doc.md, CardList.Item)
      grant_state_text: item.grant_state !== undefined ? enumText(GrantState, item.grant_state) : null,
      valid_from: fmt(item.valid_from),
      valid_to: fmt(item.valid_to),
      active_uids: (item.active_uids || []).map(mapCredential),
      replaced: (item.replaced || []).map(mapCredential),
      blocked_uids: (item.blocked_uids || []).map(mapCredential),
      // Единый список всех карт субъекта для колонки «Карты» (0 active / 1 replaced / 2 blocked).
      cards: [].concat(withState(item.active_uids, 0), withState(item.replaced, 1), withState(item.blocked_uids, 2))
    }))
  }

  // Одна карта + субъект. -> объект или null.
  async getCredential (placeId, credentialId) {
    const x = await this.cardEndpoint.getCredential(placeId, credentialId)
    if (!x) {
      this.error = envelopeError(this.cardEndpoint.lastError)
      return null
    }
    this.error = null
    return Object.assign({}, mapCredential(x), {
      state_text: enumText(CredentialState, x.state),
      subject: x.subject || null
    })
  }

  // -> {ok: true, data: {subject_id, pass_id, revision}} | {ok: false, error}
  async upsertPass (placeId, form) {
    return this.passEndpoint.upsertPass(placeId, form)
  }

  // -> {ok: true, data: {pass_id, revision}} | {ok: false, error}
  async revokePass (placeId, subjectRef) {
    return this.passEndpoint.revokePass(placeId, subjectRef)
  }

  // -> {ok: true, url: objectURL} | {ok: false, error}
  async getCardQr (placeId, cardUidHex) {
    return this.cardEndpoint.getCardQr(placeId, cardUidHex)
  }

  // Блокировка/разблокировка отдельной карты. -> {ok: true, data: {revision}} | {ok: false, error}
  async setCredentialState (placeId, credentialId, state) {
    return this.cardEndpoint.setCredentialState(placeId, credentialId, state)
  }
}
